import { deviceAdminApi } from '@/api';
import { RS485ReaderType } from '@/model/DeviceAdminModel';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, InputNumber } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import React, { useContext, useEffect, useState } from 'react';
import { noneDecimalFormatter } from '../../../../../../../../../Helper';
import { SubPermissions, User } from '../../../../../../../../../model/AccountModel';
import { InformationMessages, ModalConfirmation, ModalWarning } from '../../../../../../../../common';
import { DoorStoreSingleContext } from '../../../../../../DoorModal/DoorStoreSingleContext';
import { ReaderVersions } from '../../../../../Tabs/sections/RS485Version/RS485Body/RS485Body';
import { UpdateFirmwareModal } from '../../../../UpdateFirmwareModal/UpdateFirmwareModal';
import { setExitReaderOSDPAddress, setExitReaderOkDegrade } from '../../../../exitReaderContext';
import styles from '../../../General/general.module.scss';

type Props = {
	handleDownloadFirmware: () => void;
};

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const userCanUpdateFirmware = User.getSubComponentPermission(user, SubPermissions.Reader_FirmwareUpdateTSReader).allowed;
const style: React.CSSProperties = { color: '#faad14' };

const RS485Body: React.FC<Props> = ({ handleDownloadFirmware }) => {
	//#region State Management Variables
	const {
		contextStateDoor: {
			exitReader: {
				isFICAMEnabled,
				updateFirmwarePermissionText,
				canUpdateFirmware,
				oSDPAddress,
				firmware,
				appVersion,
				displayBoardVersion,
				okDegrade,
				selections: { rS485ReaderType },
				isVeridtReaderUpdateAllowed,
			},
		},
		dispatcherDoor,
	} = useContext(DoorStoreSingleContext);
	const [showModal, setShowModal] = useState<boolean>(false);
	const [firmwareTitleMessage, setFirmwareTitleMessage] = useState<string>();
	const [warningMessage, setWarningMessage] = useState<string[]>();
	const [isVeridtReader, setIsVeridtReader] = useState<boolean>(false);
	//#endregion

	useEffect(() => {
		setIsVeridtReader(rS485ReaderType === RS485ReaderType.VeridtPIVAUTH || rS485ReaderType === RS485ReaderType.VeridtPIVAUTHBIO);
		fetchModalMessages();
	}, [rS485ReaderType]);

	const fetchModalMessages = async () => {
		const firmwareConfirmResponse = await deviceAdminApi.getFirmwareConfirmString(rS485ReaderType);
		const firmwareTitleResponse = await deviceAdminApi.getFirmwareTitleString(rS485ReaderType);
		setFirmwareTitleMessage(firmwareTitleResponse.Entity);
		setWarningMessage(firmwareConfirmResponse.Entity.split('\n'));
	};

	const handleOnChangeAddress = (value: React.Key) => {
		if (value === '' || value === null || !Number(value.toString())) {
			if (value != 0) {
				return;
			}
		}

		dispatcherDoor(setExitReaderOSDPAddress(Number(value.toString())));
	};

	const handleOnClickUpdateFirmware = async () => {
		if (userCanUpdateFirmware) {
			const content: JSX.Element = <InformationMessages messages={warningMessage} />;

			ModalConfirmation({
				title: firmwareTitleMessage,
				content: content,
				onConfirm: () => setShowModal(true),
				okText: _('Yes'),
				cancelText: _('No'),
				width: '700px',
				icon: <ExclamationCircleOutlined style={style} />,
			});
		} else {
			const content: JSX.Element = <InformationMessages messages={[updateFirmwarePermissionText]} />;

			ModalWarning({
				okText: _('Ok'),
				content: content,
				width: '600px',
			});
		}
	};

	const handleOnSetOkDegrade = (value: CheckboxChangeEvent) => {
		dispatcherDoor(setExitReaderOkDegrade(value.target.checked));
	};

	const handleOnCloseUpdateFirmwareModal = () => {
		setShowModal(false);
	};

	const showUpdateFirmwareButton = (canUpdateFirmware && !isVeridtReader) || (canUpdateFirmware && isVeridtReader && isVeridtReaderUpdateAllowed);

	return (
		<>
			<div className={styles.sections}>
				<span>{_('OSDPAddress')}</span>
				<div>
					<InputNumber
						key='osdpAddress'
						min={0}
						max={126}
						maxLength={3}
						value={oSDPAddress}
						onChange={handleOnChangeAddress}
						id={'osdpAddress'}
						className={styles.input}
						formatter={noneDecimalFormatter}
					/>
				</div>
			</div>

			{isFICAMEnabled && (
				<div className={styles.sections}>
					<div className={styles.okDegrade}>
						<Checkbox checked={okDegrade} onChange={handleOnSetOkDegrade} id={'okDegrade'}>
							<span>{_('AllowAccessInDegrade')}</span>
						</Checkbox>
					</div>
				</div>
			)}
			<div className={styles.doubleColumn}>
				<ReaderVersions rS485ReaderType={rS485ReaderType} firmware={firmware} appVersion={appVersion} displayBoardVersion={displayBoardVersion} />
				<div className={styles.rightColumn}>
					{showUpdateFirmwareButton && (
						<Button
							type='primary'
							id='exitReaderModalUpdateReaderFirmwareButton'
							onClick={handleOnClickUpdateFirmware}
							disabled={rS485ReaderType === -1}>
							{_('UpdateReaderFirmware')}
						</Button>
					)}
				</div>
			</div>
			<UpdateFirmwareModal
				visible={showModal}
				firmwareTitle={firmwareTitleMessage}
				isVeridtReader={isVeridtReader}
				closeModal={handleOnCloseUpdateFirmwareModal}
				handleDownloadFirmware={handleDownloadFirmware}
			/>
		</>
	);
};

export { RS485Body };
